import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private _storage: Storage | null = null;
  private initialized$ = new BehaviorSubject<boolean>(false);

  constructor(private storage: Storage) {
    this.init();
  }

  /**
   * Initialize the storage driver
   */
  async init() {
    // Only create storage once
    if (this._storage !== null) {
      return;
    }
    
    try {
      // Create the storage instance
      this._storage = await this.storage.create();
      console.log('Storage initialized successfully');
      this.initialized$.next(true);
    } catch (error) {
      console.error('Error initializing storage', error);
    }
  }

  /**
   * Get data from storage
   */
  public get<T>(key: string): Observable<T | null> {
    return this.initialized$.pipe(
      // Wait until storage is initialized
      filter(initialized => initialized),
      take(1),
      switchMap(() => {
        if (!this._storage) {
          console.warn('Storage not initialized when trying to get:', key);
          return of(null);
        }
        return from(this._storage.get(key) || Promise.resolve(null));
      }),
      catchError(error => {
        console.error(`Error getting ${key} from storage`, error);
        return of(null);
      })
    );
  }

  /**
   * Set data in storage
   */
  public set(key: string, value: any): Observable<boolean> {
    return this.initialized$.pipe(
      // Wait until storage is initialized
      filter(initialized => initialized),
      take(1),
      switchMap(() => {
        if (!this._storage) {
          console.warn('Storage not initialized when trying to set:', key);
          return of(false);
        }
        return from(this._storage.set(key, value)).pipe(
          map(() => true)
        );
      }),
      catchError(error => {
        console.error(`Error setting ${key} in storage`, error);
        return of(false);
      })
    );
  }

  /**
   * Remove data from storage
   */
  public remove(key: string): Observable<boolean> {
    return this.initialized$.pipe(
      // Wait until storage is initialized
      filter(initialized => initialized),
      take(1),
      switchMap(() => {
        if (!this._storage) {
          console.warn('Storage not initialized when trying to remove:', key);
          return of(false);
        }
        return from(this._storage.remove(key)).pipe(
          map(() => true)
        );
      }),
      catchError(error => {
        console.error(`Error removing ${key} from storage`, error);
        return of(false);
      })
    );
  }

  /**
   * Clear all data from storage
   */
  public clear(): Observable<boolean> {
    return this.initialized$.pipe(
      // Wait until storage is initialized
      filter(initialized => initialized),
      take(1),
      switchMap(() => {
        if (!this._storage) {
          console.warn('Storage not initialized when trying to clear');
          return of(false);
        }
        return from(this._storage.clear()).pipe(
          map(() => true)
        );
      }),
      catchError(error => {
        console.error('Error clearing storage', error);
        return of(false);
      })
    );
  }
}
